import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, categoryKeywords } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug = 'east-asia' } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug) || categories[0];
  const [activeTab, setActiveTab] = useState(slug);

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeTab),
    [activeTab]
  );

  const keywords = categoryKeywords[activeTab] || [];

  const handleTabChange = (newSlug: string) => {
    setActiveTab(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 亚洲春色`,
    description: category.description,
    url: `https://dongyihan88.cn/category/${activeTab}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '亚洲春色',
      url: 'https://dongyihan88.cn',
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${category.name} - 亚洲春色`}
        description={category.description}
        keywords={[`${category.name}`, '亚洲春色', '春季旅游', ...keywords.slice(0, 5)].join(',')}
        canonicalUrl={`https://dongyihan88.cn/category/${activeTab}`}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="pt-16">
        <div className="relative w-full h-[400px] overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            title={`${category.name} - 亚洲春色`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-6">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{category.name}</h1>
              <p className="text-white/80 text-sm max-w-lg mx-auto">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-10 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex flex-wrap items-center gap-3 mt-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 text-foreground-500">
                <p>暂无相关文章，敬请期待</p>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-2">
              {keywords.map((kw) => (
                <span
                  key={kw}
                  className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/60 whitespace-nowrap"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}